/**
 * Pure logic behind prompt suggestion cards on Eval Results (PROC-265,
 * PROC-266).
 *
 * A suggestion's `draft` is the Prompt Constructor's PromptDraft: only the
 * fields it changed, plus its `notes`. `base_prompt` is the live prompt it
 * was drafted against, in GetPromptDetails' shape. As with
 * `draft_overrides`, the draft's role/templated/technical hold a key while
 * the base holds resolved text, so both are resolved before diffing.
 */
import { PromptSuggestionKindEnum, PromptSuggestionStatusEnum } from "@api/rest/types-and-hooks";
import type {
  GetPromptDetailsOutput,
  PromptSuggestion,
  PromptSuggestionExample,
  PromptSuggestionTest,
} from "@api/rest/types-and-hooks";
import { type FieldChange, resolveKeyOrLiteral } from "@pages/Prompts/promptReviewDiff";

/** The draft fields a reviewer can edit, in display order: api-django's
 *  `EDITABLE_DRAFT_FIELDS`. */
export const SuggestionField = {
  ROLE: "role",
  TEMPLATED_INSTRUCTIONS: "templated_instructions",
  INSTRUCTIONS: "instructions",
  TECHNICAL_INSTRUCTIONS: "technical_instructions",
  INPUTS: "inputs",
  SAMPLE_OUTPUT: "sample_output",
  RESPONSE_SCHEMA: "response_schema",
} as const;
export type SuggestionFieldValue = (typeof SuggestionField)[keyof typeof SuggestionField];

const SUGGESTION_FIELDS: SuggestionFieldValue[] = Object.values(SuggestionField);

export const SUGGESTION_FIELD_LABELS: Record<SuggestionFieldValue, string> = {
  [SuggestionField.ROLE]: "Role",
  [SuggestionField.TEMPLATED_INSTRUCTIONS]: "Templated instructions",
  [SuggestionField.INSTRUCTIONS]: "Instructions",
  [SuggestionField.TECHNICAL_INSTRUCTIONS]: "Technical instructions",
  [SuggestionField.INPUTS]: "Input keys",
  [SuggestionField.SAMPLE_OUTPUT]: "Sample output",
  [SuggestionField.RESPONSE_SCHEMA]: "Response schema",
};

/** Fields whose draft value is a catalog key. */
export type KeyedField =
  | typeof SuggestionField.ROLE
  | typeof SuggestionField.TEMPLATED_INSTRUCTIONS
  | typeof SuggestionField.TECHNICAL_INSTRUCTIONS;

export type KeyedCatalogs = Partial<Record<KeyedField, { key: string; text: string }[]>>;

export interface SuggestionPrompt {
  role?: string | null;
  templated_instructions?: string | null;
  instructions?: string | null;
  technical_instructions?: string | null;
  inputs?: { key: string }[] | null;
  sample_output?: string | null;
  response_schema?: unknown;
}

export interface SuggestionDraft extends SuggestionPrompt {
  /** What the constructor changed and why. */
  notes?: string;
}

const asObject = (value: unknown): Record<string, unknown> =>
  value !== null && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, unknown>) : {};

export const suggestionDraft = (suggestion: PromptSuggestion): SuggestionDraft =>
  asObject(suggestion.draft) as SuggestionDraft;

/** Null when pipelines couldn't capture the live prompt. */
export const suggestionBase = (suggestion: PromptSuggestion): SuggestionPrompt | null =>
  suggestion.base_prompt == null ? null : (asObject(suggestion.base_prompt) as SuggestionPrompt);

/** The live prompt in a suggestion's shape: what the suggestion's PR changes,
 *  which may have moved on since `base_prompt`. */
export const livePromptForSuggestion = (prompt: GetPromptDetailsOutput): SuggestionPrompt => ({
  role: prompt.role,
  templated_instructions: prompt.templated_instructions,
  instructions: prompt.instructions,
  technical_instructions: prompt.technical_instructions,
  inputs: prompt.input_keys.map((key) => ({ key })),
  sample_output: prompt.sample_output,
  response_schema: prompt.response_schema,
});

/** PromptDraft's "leave this field alone": None or empty. */
const isUnset = (value: unknown): boolean =>
  value == null || value === "" || (Array.isArray(value) && value.length === 0);

/** The fields the draft proposes changing, in display order. */
export const proposedFields = (draft: SuggestionDraft): SuggestionFieldValue[] =>
  SUGGESTION_FIELDS.filter((field) => !isUnset(draft[field]));

/** Whether any draft carries a keyed field, so its catalog must load before
 *  diffing — the raw key would otherwise diff against resolved text. */
export const needsCatalog = (drafts: SuggestionDraft[], field: KeyedField): boolean =>
  drafts.some((draft) => !isUnset(draft[field]));

const inputsText = (inputs: { key: string }[] | null | undefined): string =>
  (inputs ?? []).map((input) => input.key).join("\n");

export const schemaText = (schema: unknown): string => {
  if (schema == null) return "";
  if (typeof schema === "string") return schema;
  return JSON.stringify(schema, null, 2);
};

/** Trailing whitespace dropped: a field ending in a newline on one side only
 *  isn't a change. */
const fieldText = (prompt: SuggestionPrompt, field: SuggestionFieldValue, catalogs: KeyedCatalogs): string =>
  rawFieldText(prompt, field, catalogs).trimEnd();

const rawFieldText = (prompt: SuggestionPrompt, field: SuggestionFieldValue, catalogs: KeyedCatalogs): string => {
  switch (field) {
    case SuggestionField.ROLE:
    case SuggestionField.TEMPLATED_INSTRUCTIONS:
    case SuggestionField.TECHNICAL_INSTRUCTIONS:
      return resolveKeyOrLiteral(prompt[field], catalogs[field]);
    case SuggestionField.INPUTS:
      return inputsText(prompt.inputs);
    case SuggestionField.RESPONSE_SCHEMA:
      return schemaText(prompt.response_schema);
    default:
      return prompt[field] ?? "";
  }
};

/** One diff per field the draft changes. Fields equal to the live prompt are
 *  left out. With no base, every proposed field diffs against empty. */
export const suggestionChanges = (
  base: SuggestionPrompt | null,
  draft: SuggestionDraft,
  catalogs: KeyedCatalogs,
): FieldChange[] =>
  proposedFields(draft).flatMap((field) => {
    const before = base ? fieldText(base, field, catalogs) : "";
    const after = fieldText(draft, field, catalogs);
    if (before === after) return [];
    return [
      {
        label: SUGGESTION_FIELD_LABELS[field],
        before,
        after,
        format: field === SuggestionField.RESPONSE_SCHEMA ? ("json" as const) : ("text" as const),
      },
    ];
  });

/** "1 of 3 suggestions to review"; `index` is 0-based. */
export const reviewPositionLabel = (index: number, total: number): string =>
  total === 1 ? "1 suggestion to review" : `${index + 1} of ${total} suggestions to review`;

export const SUGGESTION_STATUS_LABELS: Record<PromptSuggestionStatusEnum, string> = {
  [PromptSuggestionStatusEnum.new]: "New",
  [PromptSuggestionStatusEnum.testing]: "Testing",
  [PromptSuggestionStatusEnum.approved]: "Approved",
  [PromptSuggestionStatusEnum.incorporated]: "Incorporated",
  [PromptSuggestionStatusEnum.shipped]: "Shipped",
  [PromptSuggestionStatusEnum.dismissed]: "Dismissed",
};

/** Example edits shown before "Show all". */
export const EXAMPLES_SHOWN = 2;

export const ExampleKind = {
  CHANGED: "changed",
  DELETED: "deleted",
  ADDED: "added",
} as const;
type ExampleKindValue = (typeof ExampleKind)[keyof typeof ExampleKind];

export const EXAMPLE_KIND_LABELS: Record<ExampleKindValue, string> = {
  [ExampleKind.CHANGED]: "Changed",
  [ExampleKind.DELETED]: "Deleted",
  [ExampleKind.ADDED]: "Added",
};

/** AI text only is a deletion, user text only an addition. */
export const exampleKind = (example: PromptSuggestionExample): ExampleKindValue => {
  if (example.ai_text && example.user_text) return ExampleKind.CHANGED;
  return example.ai_text ? ExampleKind.DELETED : ExampleKind.ADDED;
};

/** "25 changed · 114 deleted · 0 added": the edits the examples were picked
 *  from, by kind, in the examples' order. Null when pipelines sent no counts.
 *  Changed edits are favoured as examples, so this keeps the mix honest. */
export const exampleCountsLabel = (suggestion: PromptSuggestion): string | null => {
  const counts: [number | null, ExampleKindValue][] = [
    [suggestion.category_substitution_count, ExampleKind.CHANGED],
    [suggestion.category_deletion_count, ExampleKind.DELETED],
    [suggestion.category_addition_count, ExampleKind.ADDED],
  ];
  if (counts.every(([count]) => count === null)) return null;
  return counts
    .map(([count, kind]) => `${(count ?? 0).toLocaleString()} ${EXAMPLE_KIND_LABELS[kind].toLowerCase()}`)
    .join(" · ");
};

export interface DraftFormValues {
  role: string;
  templated_instructions: string;
  instructions: string;
  technical_instructions: string;
  input_keys: string[];
  sample_output: string;
  response_schema: string;
}

export const draftFormValues = (draft: SuggestionDraft): DraftFormValues => ({
  role: draft.role ?? "",
  templated_instructions: draft.templated_instructions ?? "",
  instructions: draft.instructions ?? "",
  technical_instructions: draft.technical_instructions ?? "",
  input_keys: (draft.inputs ?? []).map((input) => input.key),
  sample_output: draft.sample_output ?? "",
  response_schema: schemaText(draft.response_schema),
});

export type DraftEditsResult = { draft: Record<string, unknown> } | { error: string };

/** The edited fields in the draft's shape, for updatePromptSuggestionDraft,
 *  which merges them into the stored draft. A cleared field is sent as null:
 *  the draft then leaves the live value alone. */
export const draftEdits = (
  fields: SuggestionFieldValue[],
  values: DraftFormValues,
  initial: DraftFormValues,
): DraftEditsResult => {
  const draft: Record<string, unknown> = {};
  for (const field of fields) {
    if (field === SuggestionField.INPUTS) {
      if (values.input_keys.join("\n") !== initial.input_keys.join("\n")) {
        draft[field] = values.input_keys.length > 0 ? values.input_keys.map((key) => ({ key })) : null;
      }
      continue;
    }
    if (values[field] === initial[field]) continue;
    if (field === SuggestionField.RESPONSE_SCHEMA) {
      if (!values[field].trim()) {
        draft[field] = null;
        continue;
      }
      try {
        draft[field] = JSON.parse(values[field]);
      } catch {
        return { error: "Response schema is not valid JSON." };
      }
      continue;
    }
    draft[field] = values[field] || null;
  }
  return { draft };
};

/** Why a suggestion needs re-drafting: api-django's `REDRAFT_*`. */
const RedraftReason = {
  SIBLING_PENDING: "sibling_pending",
  SIBLING_SHIPPED: "sibling_shipped",
  PROMPT_CHANGED: "prompt_changed",
} as const;

/** How often a page polls while Rio re-drafts a suggestion. */
export const REDRAFT_POLL_MS = 5000;

/** A stale or re-drafting suggestion can only be re-drafted or dismissed;
 *  one being incorporated into another, neither. */
export const needsRedraft = (suggestion: PromptSuggestion): boolean =>
  suggestion.redraft_reason != null || suggestion.redraft_in_progress || suggestion.being_incorporated_into != null;

/** Theme titles in quotes, as a list. */
const quotedTitles = (suggestions: readonly { theme_title: string }[]): string =>
  suggestions.map((s) => `“${s.theme_title}”`).join(", ");

/** The card's line on re-drafting, or null when there's nothing to say. */
export const redraftNote = (suggestion: PromptSuggestion, draft: SuggestionDraft): string | null => {
  if (suggestion.being_incorporated_into) {
    return `Rio is incorporating this into ${quotedTitles([suggestion.being_incorporated_into])}. It leaves this list once that draft is ready.`;
  }
  if (suggestion.redraft_in_progress && suggestion.incorporating.length > 0) {
    return `Rio is combining ${quotedTitles(suggestion.incorporating)} into this draft, on the prompt as it is now…`;
  }
  if (suggestion.redraft_in_progress) return "Rio is re-drafting this on the prompt as it is now…";
  if (suggestion.redraft_reason === RedraftReason.SIBLING_PENDING) {
    return "Another suggestion for this prompt opened a PR. Once it's merged and live, re-draft this on top of it, or dismiss it.";
  }
  if (suggestion.redraft_reason === RedraftReason.SIBLING_SHIPPED) {
    return "Another suggestion for this prompt is now live, so this draft was written on an older prompt. Re-draft it to build on that change, or dismiss it.";
  }
  if (suggestion.redraft_reason === RedraftReason.PROMPT_CHANGED) {
    return "The prompt changed since Rio drafted this. Re-draft it on the prompt as it is now, or dismiss it.";
  }
  if (suggestion.incorporated.length > 0) {
    const next = suggestion.status === PromptSuggestionStatusEnum.new ? " Test it, then open the PR." : "";
    return `This draft now also covers ${quotedTitles(suggestion.incorporated)}.${next}`;
  }
  if (suggestion.redrafted_at == null) return null;
  if (proposedFields(draft).length === 0) {
    return "Rio re-drafted this and found nothing left to change: the prompt already covers it. Dismiss it?";
  }
  return `Rio re-drafted this on ${new Date(suggestion.redrafted_at).toLocaleDateString()}, on top of the prompt's latest changes.`;
};

/** Why the suggestion can't be approved, or null. A stale one says so in its
 *  re-draft note and a shipped one shows its PRs instead. */
export const approvalBlocker = (suggestion: PromptSuggestion): string | null => {
  const open =
    suggestion.status === PromptSuggestionStatusEnum.new || suggestion.status === PromptSuggestionStatusEnum.testing;
  if (suggestion.can_open_pr || !open || needsRedraft(suggestion)) return null;
  return suggestion.open_pr_blocker;
};

/** The card's kind label: which prompt the suggestion edits. */
export const SUGGESTION_KIND_LABELS: Record<PromptSuggestionKindEnum, string> = {
  [PromptSuggestionKindEnum.edit_org_prompt]: "Org's own prompt",
  [PromptSuggestionKindEnum.org_copy]: "Org copy",
  [PromptSuggestionKindEnum.shared_change]: "Shared prompt",
};

/** Shared prompts with open suggestions from more than one org: those are
 *  reviewed together from Eval Results, not on each org's page. */
export const multiOrgSharedGroups = <T extends { suggestions: readonly { organization_id: number }[] }>(
  groups: readonly T[],
): T[] => groups.filter((g) => new Set(g.suggestions.map((s) => s.organization_id)).size > 1);

/** A suggestion that can't be included or dismissed while Rio re-drafts it or
 *  folds it into another. */
export const suggestionBusy = (suggestion: PromptSuggestion): boolean =>
  suggestion.redraft_in_progress || suggestion.being_incorporated_into != null;

export const SharedChoice = {
  INCLUDE: "include",
  DISMISS: "dismiss",
} as const;
export type SharedChoiceValue = (typeof SharedChoice)[keyof typeof SharedChoice];

/** The shared pop-up's outcome, or null until every suggestion has a choice:
 *  the first included, which is the strongest, takes in the rest. */
export const sharedCombineRequest = (
  suggestions: readonly { id: number }[],
  choices: Record<number, SharedChoiceValue | undefined>,
): { parentId: number | null; incorporate_ids: number[]; dismiss_ids: number[] } | null => {
  if (suggestions.length === 0 || suggestions.some((s) => choices[s.id] === undefined)) return null;
  const included = suggestions.filter((s) => choices[s.id] === SharedChoice.INCLUDE).map((s) => s.id);
  return {
    parentId: included[0] ?? null,
    incorporate_ids: included.slice(1),
    dismiss_ids: suggestions.filter((s) => choices[s.id] === SharedChoice.DISMISS).map((s) => s.id),
  };
};

/** Who an approval reaches: a shared change edits the shared prompt
 *  for every org on it; the rest reach the suggestion's org only. */
export const approvalScopeLabel = (suggestion: PromptSuggestion, orgName: string): string =>
  suggestion.kind === PromptSuggestionKindEnum.shared_change
    ? "Applies to every organization on this prompt"
    : `Applies to ${orgName} only`;

/** What approving does, by kind. */
export const approvalEffect = (suggestion: PromptSuggestion, orgName: string): string => {
  switch (suggestion.kind) {
    case PromptSuggestionKindEnum.org_copy:
      return `This prompt is shared across customers. Approving creates a copy just for ${orgName} with this change, and drafts of ${orgName}'s own templates that use the copy. From then on, fixes to the shared prompt don't reach ${orgName}'s copy. The template team tests and publishes those drafts.`;
    case PromptSuggestionKindEnum.shared_change:
      return "This prompt is on a shared template. Approving changes it for every organization that uses it, so get their OK first.";
    default:
      return `Approving changes ${orgName}'s prompt.`;
  }
};

/** Review draft's pop-up: what to do with each of the prompt's other open suggestions. */
export const IncorporateChoice = {
  INCORPORATE: "incorporate",
  DISMISS: "dismiss",
} as const;
export type IncorporateChoiceValue = (typeof IncorporateChoice)[keyof typeof IncorporateChoice];

/** The pop-up's request, or null until every suggestion has a choice. */
export const incorporateRequest = (
  incorporable: readonly { id: number }[],
  choices: Record<number, IncorporateChoiceValue | undefined>,
): { incorporate_ids: number[]; dismiss_ids: number[] } | null => {
  if (incorporable.some((s) => choices[s.id] === undefined)) return null;
  return {
    incorporate_ids: incorporable.filter((s) => choices[s.id] === IncorporateChoice.INCORPORATE).map((s) => s.id),
    dismiss_ids: incorporable.filter((s) => choices[s.id] === IncorporateChoice.DISMISS).map((s) => s.id),
  };
};

/** The query param that opens a prompt's Evaluations page on one suggestion. */
export const SUGGESTION_PARAM = "suggestion";

/** The prompt's Evaluations page in suggestion mode (PROC-266): its draft,
 *  tests and Rio thread are the suggestion's. */
export const suggestionEvaluationsPath = (promptKey: string, suggestionId: number): string =>
  `/prompts/${encodeURIComponent(promptKey)}/evaluations?${SUGGESTION_PARAM}=${suggestionId}`;

/** The prompt's Review draft page for one suggestion, where Open PR approves it. */
export const suggestionReviewPath = (promptKey: string, suggestionId: number): string =>
  `/prompts/${encodeURIComponent(promptKey)}/review-draft?${SUGGESTION_PARAM}=${suggestionId}`;

/** A before/after for the customer summary from one of the suggestion's
 *  tests: the judge's quotes of the improvements it found, written on a test
 *  profile (a made-up learner). Null when the test has none to quote. */
export const customerExample = (test: PromptSuggestionTest): string | null => {
  const quoted = test.pairwise_differences
    .filter((d) => d.effect === "better" && (d.current_quote || d.candidate_quote))
    .slice(0, 2);
  if (quoted.length === 0) return null;
  const lines = ["Example:"];
  quoted.forEach((d) => {
    lines.push("", "Before:", d.current_quote ? `"${d.current_quote}"` : "(not there)");
    lines.push("", "After:", d.candidate_quote ? `"${d.candidate_quote}"` : "(removed)");
  });
  return lines.join("\n");
};

/** The judge's verdict on the draft against the live prompt. */
export const TEST_VERDICT_LABELS: Record<string, string> = {
  same: "Same as live",
  better: "Draft better",
  worse: "Draft worse",
  unsure: "Judge unsure",
};

/** How often a page polls a suggestion's tests while one is running or being judged. */
export const SUGGESTION_TEST_POLL_MS = 4000;

// A test still settling past this is stuck, not running: stop polling it.
const TEST_POLL_WINDOW_MS = 30 * 60 * 1000;

/** Whether either run failed, so the test will never get a verdict. */
export const testRunFailed = (test: PromptSuggestionTest): boolean =>
  test.live_status === "failed" || test.draft_status === "failed";

/** Whether a test's runs or its judge are still working, so the list should
 *  keep polling. Both runs complete with no verdict yet means the judge is
 *  about to start or running. */
export const testInFlight = (test: PromptSuggestionTest, now: number = Date.now()): boolean => {
  if (now - new Date(test.created_date).getTime() > TEST_POLL_WINDOW_MS) return false;
  if (testRunFailed(test)) return false;
  if (test.live_status === "running" || test.draft_status === "running") return true;
  return test.live_status === "complete" && test.draft_status === "complete" && test.pairwise_verdict === null
    ? test.pairwise_status !== "failed" && test.pairwise_status !== "complete"
    : false;
};

/** The fields a test's draft changed, as labels: which draft it tested. */
export const testedFieldLabels = (test: PromptSuggestionTest): string[] =>
  proposedFields(asObject(test.draft) as SuggestionDraft).map((field) => SUGGESTION_FIELD_LABELS[field]);
